// Physics → Section 8: Energy and Matter (KS3, sourced from the
// Grade9Ready science PDF). 9 topics as specified by the user.
// Same rich-content template as the other section files.
import authored from './authoredTopics.json';

const AUTHORED_KEY = 'physics/energy-and-matter';
const AUTHORED = (authored && authored[AUTHORED_KEY]) || [];
const AUTHORED_BY_ID = Object.fromEntries(AUTHORED.map((t) => [t.id, t]));

// =====================================================================
// 8.1  ENERGY TRANSFER
// =====================================================================
const ENERGY_TRANSFER_TOPIC = {
  id: 'phys-8.1',
  title: 'Energy Transfer',
  slug: 'energy-transfer',
  pageRef: 'Section 8 · Topic 1 (pp 102–104)',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Energy stores',
        body:
          'Energy is stored in different ways. The main GCSE stores you must know: kinetic (anything moving), gravitational potential (anything raised up), elastic (stretched/squashed springs), thermal (hot objects), chemical (food, fuel, batteries), magnetic and electrostatic (magnets and charges), and nuclear (inside an atom\u2019s nucleus). Energy is measured in joules (J).',
        check: {
          question: 'A stretched elastic band stores energy mostly in which type of store?',
          options: ['Kinetic', 'Elastic', 'Gravitational', 'Thermal'],
          answer: 'Elastic',
          explainer: 'Stretched or squashed springs/bands hold energy in the elastic store.',
        },
      },
      {
        heading: 'Energy transfers',
        body:
          'Energy is never used up — it is just transferred from one store to another. The transfer can happen by: (1) mechanical work (pushing, pulling, lifting), (2) electrical work (current flowing in a circuit), (3) heating (energy moves because of a temperature difference) or (4) radiation (e.g. light, infrared waves). When we say "the kettle uses energy", what we really mean is energy is being transferred from chemical (gas/electricity supply) to thermal (hot water).',
        check: {
          question: 'When you lift a box up onto a shelf, energy is transferred from your chemical store to the box\u2019s ___ store.',
          options: ['Kinetic', 'Thermal', 'Gravitational potential', 'Elastic'],
          answer: 'Gravitational potential',
          explainer: 'Lifting raises the box\u2019s height — energy is now stored gravitationally.',
        },
      },
      {
        heading: 'Energy diagrams',
        body:
          "A useful way to show what's happening is an arrow diagram: starting store → method → ending store(s). Example: a torch on. Starting store: chemical (battery). Method: electrical work + radiation. Ending stores: thermal (warm bulb) + light/radiation (escapes the torch). Notice: more than one ending store usually exists — some energy always ends up in the surroundings as heat.",
        check: {
          question: 'A car drives along. The chemical store of the petrol decreases. Where does most of the energy end up?',
          options: ['Gravitational store of the car', 'Kinetic + thermal stores (and surroundings as heat)', 'Elastic store', 'Light store'],
          answer: 'Kinetic + thermal stores (and surroundings as heat)',
          explainer: "Petrol's chemical store transfers to KE of car, plus thermal heating of engine, road, and air.",
        },
      },
    ],
    keyPoints: [
      'Energy stores: kinetic, GPE, elastic, thermal, chemical, magnetic, electrostatic, nuclear',
      'Energy is measured in joules (J)',
      'Energy is never used up — it is transferred between stores',
      'Four transfer methods: mechanical work, electrical, heating, radiation',
      'Some energy always ends up in the surroundings as heat',
    ],
    workedExamples: [
      {
        problem: 'A roller-coaster car at the top of the first hill is at rest. As it speeds down the slope, describe the main energy transfer.',
        steps: [
          { step: 1, instruction: 'Starting store', working: 'At the top, at rest → mostly gravitational potential' },
          { step: 2, instruction: 'During fall', working: 'Height drops, speed rises → GPE → kinetic' },
          { step: 3, instruction: 'Plus losses', working: 'Friction with rails + air → some thermal energy in surroundings' },
        ],
        answer: 'Gravitational store → kinetic store (with some loss to thermal).',
        commonMistake: 'Saying ALL the energy goes to kinetic — friction always takes some as thermal.',
      },
    ],
    whyItWorks:
      'Thinking in stores and transfers stops you saying confusing things like "the energy is used up". Nothing is created or destroyed — it just moves. That insight unlocks every Physics calculation that follows.',
  },
  glossary: [
    { term: 'Energy store', definition: 'A way energy is held — kinetic, gravitational, chemical, etc.' },
    { term: 'Joule (J)', definition: 'The SI unit of energy. 1 J is roughly the energy to lift 100 g by 1 m.' },
    { term: 'Mechanical work', definition: 'Energy transfer when a force moves through a distance (push/pull/lift).' },
    { term: 'Electrical work', definition: 'Energy transfer carried by current through a circuit.' },
    { term: 'Radiation', definition: 'Energy transfer through waves (light, infrared, etc.).' },
  ],
  examQuestion: {
    prompt:
      '(a) Name FOUR ways energy can be transferred. (b) For a child kicking a football into the back of the net, describe the main energy transfers. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Mechanical work / electrical / heating / radiation (any 4)',
      'Chemical store (in muscles) decreases',
      'Kinetic store of ball increases',
      'Some thermal energy is dissipated to surroundings (friction with air, foot)',
      'Net catches the ball — kinetic transfers into elastic of net + thermal',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-8.1.w1', type: 'text', question: 'Energy is measured in ___ .', answer: 'joules', hint1: '6 letters', hint2: 'Plural', hint3: 'Symbol J' },
      { id: 'phys-8.1.w2', type: 'choice', question: 'A stretched spring holds energy in which store?', options: ['Kinetic', 'Elastic', 'Thermal', 'Chemical'], answer: 'Elastic', hint1: 'Stretched/squashed', hint2: '7 letters', hint3: 'Like a band' },
      { id: 'phys-8.1.w3', type: 'choice', question: 'Lifting a box up uses energy to fill which store?', options: ['Chemical', 'Gravitational potential', 'Elastic', 'Magnetic'], answer: 'Gravitational potential', hint1: 'Linked to height', hint2: 'GPE', hint3: 'Above the ground' },
    ],
    level1: [
      { id: 'phys-8.1.1', type: 'choice', question: 'Energy is never destroyed — it is just…', options: ['Used up', 'Created', 'Transferred', 'Disappears'], answer: 'Transferred', hint1: 'Moves between stores', hint2: '11 letters', hint3: 'Conservation' },
      { id: 'phys-8.1.2', type: 'text', question: 'A moving car has lots of energy in its ___ store.', answer: 'kinetic', hint1: '7 letters', hint2: 'Movement-related', hint3: 'Starts with k' },
    ],
    level2: [
      { id: 'phys-8.1.3', type: 'choice', question: 'A torch shining: chemical (battery) → electrical work → ?', options: ['Kinetic', 'Thermal + light', 'Elastic', 'Magnetic'], answer: 'Thermal + light', hint1: 'Bulb gets warm', hint2: 'Plus light', hint3: 'Two stores' },
      { id: 'phys-8.1.4', type: 'choice', question: 'A ball thrown up. At the highest point, KE is at minimum and GPE is at…', options: ['Minimum', 'Maximum', 'Zero', 'Halfway'], answer: 'Maximum', hint1: 'Highest height', hint2: 'Most GPE', hint3: 'Top of arc' },
    ],
    level3: [
      { id: 'phys-8.1.5', type: 'choice', question: 'Why does some energy always end up in the surroundings as heat?', options: ['Heat is the easiest store to fill', 'Some energy is always dissipated by friction/air resistance', 'Energy is created', 'It is random'], answer: 'Some energy is always dissipated by friction/air resistance', hint1: 'Practical losses', hint2: 'No 100% efficient machines', hint3: 'Friction does work' },
      { id: 'phys-8.1.6', type: 'text', question: 'Pushing a box across a table is an example of mechanical ___ .', answer: 'work', hint1: '4 letters', hint2: 'Force × distance', hint3: 'Starts with w' },
    ],
  },
};

// =====================================================================
// 8.2  ENERGY TRANSFER BY HEATING
// =====================================================================
const HEATING_TOPIC = {
  id: 'phys-8.2',
  title: 'Energy Transfer by Heating',
  slug: 'energy-transfer-by-heating',
  pageRef: 'Section 8 · Topic 2',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Three ways heat moves',
        body:
          'Heat (energy from a hotter object to a cooler one) travels by three methods. (1) Conduction — through solids; particles vibrate harder and pass the energy on. (2) Convection — in liquids and gases; warm fluid rises, cooler fluid sinks, setting up a circulating current. (3) Radiation — as infrared waves through empty space (e.g. the Sun warming the Earth). Different materials prefer different methods.',
        check: {
          question: 'How does heat from the Sun reach Earth across empty space?',
          options: ['Conduction', 'Convection', 'Radiation', 'Magic'],
          answer: 'Radiation',
          explainer: 'No particles in space → only radiation can travel through a vacuum.',
        },
      },
      {
        heading: 'Conductors vs insulators',
        body:
          'Metals are great heat conductors — they have free electrons that can carry energy quickly. That\u2019s why a metal mug feels colder to touch even when it isn\u2019t (heat flows out of your hand fast). Wood, plastic, fabric and air are insulators — they carry heat slowly. Trapping air (e.g. in foam, fluffy clothing) is a brilliant way to insulate, because still air conducts very little.',
        check: {
          question: 'Why are metal saucepans usually given plastic handles?',
          options: ['Looks nice', 'Plastic is a better insulator — handle stays cool', 'Plastic is cheap', 'Plastic is heavier'],
          answer: 'Plastic is a better insulator — handle stays cool',
          explainer: 'Plastic conducts heat poorly, keeping the handle safe to grip.',
        },
      },
      {
        heading: 'Reducing heat loss in a home',
        body:
          'Most UK heat loss in a typical house: through the roof (~25%), walls (~35%), floors and windows. Solutions: loft insulation traps a thick layer of still air; cavity wall insulation fills the wall gap; double glazing puts argon between two panes (poor conductor and slows convection); thick curtains reduce radiation; carpet reduces conduction down through the floor. All of these save energy bills AND reduce CO₂ emissions.',
        check: {
          question: 'Double glazing reduces heat loss MAINLY because…',
          options: ['Two panes look fancier', 'A trapped layer of gas conducts heat poorly', 'Glass is a metal', 'Windows are smaller'],
          answer: 'A trapped layer of gas conducts heat poorly',
          explainer: 'The gas (often argon) is a poor conductor, slowing heat flow through the window.',
        },
      },
    ],
    keyPoints: [
      'Three ways heat moves: conduction, convection, radiation',
      'Conduction: through solids (metals = best)',
      'Convection: in liquids/gases (warm rises, cool sinks)',
      'Radiation: through empty space as infrared waves',
      'Metals = great conductors; air, plastic, wool = good insulators',
      'Home insulation: loft, cavity walls, double glazing, carpet, curtains',
    ],
    workedExamples: [
      {
        problem: 'Explain why a vacuum flask keeps a drink hot for hours, using all three heat-transfer terms.',
        steps: [
          { step: 1, instruction: 'Conduction', working: 'Vacuum between walls = no particles → almost no conduction' },
          { step: 2, instruction: 'Convection', working: 'No air movement in vacuum → no convection currents' },
          { step: 3, instruction: 'Radiation', working: 'Silvered walls reflect infrared radiation back into the drink' },
        ],
        answer: 'Vacuum stops conduction + convection; shiny silver coatings reduce radiation. All three pathways are blocked.',
        commonMistake: 'Forgetting the silvered surface — it\u2019s key to reducing radiation losses.',
      },
    ],
    whyItWorks:
      'Heat always flows from hot to cold. Stopping it requires blocking all three pathways. That\u2019s why insulation is layered — different layers stop different transfer types.',
  },
  glossary: [
    { term: 'Conduction', definition: 'Heat transfer through a solid as particles vibrate and pass energy on.' },
    { term: 'Convection', definition: 'Heat transfer in a liquid or gas through warm material rising and cool sinking.' },
    { term: 'Radiation', definition: 'Heat transfer by infrared waves; works in a vacuum.' },
    { term: 'Conductor', definition: 'A material that lets heat travel quickly through it.' },
    { term: 'Insulator', definition: 'A material that slows down heat flow.' },
  ],
  examQuestion: {
    prompt:
      '(a) State the THREE ways heat is transferred. (b) Explain why warm air rises in a room. (c) Suggest TWO ways a homeowner could reduce heat loss from their home. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Conduction, convection, radiation',
      'Warm air is less dense than cold air; less dense fluid rises, denser sinks → convection current',
      'Loft insulation / cavity wall insulation / double glazing',
      'Carpets / curtains / draught-proofing (any 2 sensible)',
      "(Don't accept 'turn down the thermostat' as that's reducing demand, not reducing loss)",
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-8.2.w1', type: 'text', question: 'Heat through a solid = ___ .', answer: 'conduction', hint1: '10 letters', hint2: 'Through solids', hint3: 'Starts with c' },
      { id: 'phys-8.2.w2', type: 'choice', question: 'Best heat conductor?', options: ['Wood', 'Plastic', 'Iron', 'Air'], answer: 'Iron', hint1: 'Metal', hint2: '4 letters', hint3: 'Magnetic' },
      { id: 'phys-8.2.w3', type: 'choice', question: 'Heat through empty space is by…', options: ['Conduction', 'Convection', 'Radiation', 'Cycling'], answer: 'Radiation', hint1: 'Infrared waves', hint2: 'No particles needed', hint3: '9 letters' },
    ],
    level1: [
      { id: 'phys-8.2.1', type: 'choice', question: 'Which is a good thermal insulator?', options: ['Copper', 'Silver', 'Wool', 'Iron'], answer: 'Wool', hint1: 'Traps still air', hint2: 'Used in jumpers', hint3: '4 letters' },
      { id: 'phys-8.2.2', type: 'text', question: 'In convection, warm fluid ___ .', answer: 'rises', hint1: '5 letters', hint2: 'Less dense → up', hint3: 'Starts with r' },
    ],
    level2: [
      { id: 'phys-8.2.3', type: 'choice', question: 'Why are vacuum flasks shiny inside?', options: ['Looks nice', 'Reflects radiation back into the drink', 'Stronger', 'Cheaper'], answer: 'Reflects radiation back into the drink', hint1: 'Silver mirror', hint2: 'Reduces radiation loss', hint3: 'Like Mylar' },
      { id: 'phys-8.2.4', type: 'choice', question: 'Why does loft insulation work?', options: ['It is heavy', 'It traps still air which conducts poorly', 'It is white', 'It is fireproof'], answer: 'It traps still air which conducts poorly', hint1: 'Air = bad conductor', hint2: 'Trap stops convection', hint3: 'Fluffy material' },
    ],
    level3: [
      { id: 'phys-8.2.5', type: 'choice', question: 'A house loses MOST of its heat through which feature?', options: ['Doors', 'Floors', 'Walls', 'Light bulbs'], answer: 'Walls', hint1: 'Largest surface area', hint2: '~35% of loss', hint3: 'Where insulation helps most' },
      { id: 'phys-8.2.6', type: 'text', question: 'Filling a cavity in a wall with foam is called ___ wall insulation.', answer: 'cavity', hint1: '6 letters', hint2: 'Hollow space', hint3: 'Starts with c' },
    ],
  },
};

// =====================================================================
// 8.3  CONSERVATION OF ENERGY
// =====================================================================
const CONSERVATION_TOPIC = {
  id: 'phys-8.3',
  title: 'Conservation of Energy',
  slug: 'conservation-of-energy',
  pageRef: 'Section 8 · Topic 3',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'The law',
        body:
          'Energy cannot be created or destroyed — it can only be transferred from one store to another, OR dissipated to the surroundings (often as heat). This is the law of conservation of energy. The TOTAL energy before equals the TOTAL energy after, every time, no exceptions.',
        check: {
          question: 'In every energy transfer, the total energy…',
          options: ['Increases', 'Decreases', 'Stays the same', 'Disappears'],
          answer: 'Stays the same',
          explainer: 'Conservation of energy: total before = total after.',
        },
      },
      {
        heading: 'Useful vs wasted energy',
        body:
          'In a real machine, only some of the input energy ends up where you want it (useful). The rest is wasted — usually dissipated as heat to the surroundings. A car engine wastes about 70-80% of fuel energy as heat. A light bulb wastes much of its electrical input as heat (which is why old filament bulbs got so hot). Designing more efficient machines means cutting wasted energy.',
        check: {
          question: 'In a normal car engine, most input energy ends up as…',
          options: ['Kinetic energy of the car', 'Sound', 'Wasted heat to the surroundings', 'Light'],
          answer: 'Wasted heat to the surroundings',
          explainer: 'Petrol engines are only ~20-30% efficient. The rest is heat dissipated through the exhaust and engine.',
        },
      },
      {
        heading: 'Efficiency',
        body:
          'Efficiency = (useful energy out ÷ total energy in) × 100%. A modern LED bulb is about 90% efficient — only 10% wasted as heat. An old filament bulb was about 5% efficient. Efficiency is always between 0 and 100%; it can never be more than 100% (you cannot get more energy out than you put in — that would break conservation of energy).',
        check: {
          question: 'A bulb takes 100 J of electrical energy and produces 5 J of light. Efficiency?',
          options: ['5%', '50%', '95%', '100%'],
          answer: '5%',
          explainer: '5/100 × 100 = 5%.',
        },
      },
    ],
    keyPoints: [
      'Energy cannot be created or destroyed',
      'It can be transferred between stores or dissipated',
      'Total energy before = total energy after',
      'Useful = where you want it; wasted = where you don\u2019t',
      'Efficiency = useful out ÷ total in × 100%',
      'Efficiency is always 0–100% (never more)',
    ],
    workedExamples: [
      {
        problem: 'A motor takes in 200 J of electrical energy. It transfers 60 J usefully to lifting a load. Calculate efficiency.',
        steps: [
          { step: 1, instruction: 'Formula', working: 'Efficiency = useful ÷ total × 100' },
          { step: 2, instruction: 'Substitute', working: '= 60 ÷ 200 × 100' },
          { step: 3, instruction: 'Calculate', working: '= 30%' },
        ],
        answer: 'Efficiency = 30%. The other 70% (140 J) is wasted as heat/sound.',
        commonMistake: 'Forgetting × 100 (giving 0.3 instead of 30%).',
      },
    ],
    whyItWorks:
      'Conservation of energy is one of the most reliable laws in all of physics. It works everywhere — falling apples, exploding stars, your phone charger. Engineers design machines to push efficiency as high as possible because every wasted joule costs money and emits CO₂.',
  },
  glossary: [
    { term: 'Conservation of energy', definition: 'Energy cannot be created or destroyed — only transferred or dissipated.' },
    { term: 'Useful energy', definition: 'Energy ending up in the store you want.' },
    { term: 'Wasted energy', definition: 'Energy ending up dissipated as heat, sound, etc.' },
    { term: 'Dissipated', definition: 'Spread out into the surroundings (usually as heat).' },
    { term: 'Efficiency', definition: '(useful out ÷ total in) × 100%.' },
  ],
  examQuestion: {
    prompt:
      'A washing machine uses 800 J of electrical energy. 480 J is transferred usefully to spin the drum. (a) State the law of conservation of energy. (b) Calculate the efficiency. (c) State where the wasted energy goes. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Energy cannot be created or destroyed; only transferred or dissipated',
      'Efficiency = 480 ÷ 800 × 100',
      '= 60%',
      'Wasted energy = 320 J',
      'Dissipated as heat (and sound) to the surroundings',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-8.3.w1', type: 'text', question: 'Energy cannot be created or ___ .', answer: 'destroyed', hint1: '9 letters', hint2: 'Conservation', hint3: 'Starts with d' },
      { id: 'phys-8.3.w2', type: 'choice', question: 'Wasted energy usually ends up as…', options: ['Light', 'Heat', 'Mass', 'Cold'], answer: 'Heat', hint1: 'Goes to surroundings', hint2: '4 letters', hint3: 'Thermal' },
      { id: 'phys-8.3.w3', type: 'number', question: '40 J useful out of 200 J input. Efficiency (%)?', answer: '20', hint1: 'Two digits', hint2: '40/200 × 100', hint3: 'One fifth' },
    ],
    level1: [
      { id: 'phys-8.3.1', type: 'choice', question: 'The MAXIMUM possible efficiency is…', options: ['50%', '90%', '99%', '100%'], answer: '100%', hint1: 'No more energy out than in', hint2: 'Conservation rule', hint3: 'Three digits' },
      { id: 'phys-8.3.2', type: 'number', question: 'Useful = 30 J, total = 60 J. Efficiency (%)?', answer: '50', hint1: 'Two digits', hint2: 'Half', hint3: '50%' },
    ],
    level2: [
      { id: 'phys-8.3.3', type: 'choice', question: 'A modern LED bulb is much more efficient than an old filament bulb because…', options: ['It uses less voltage', 'It wastes less energy as heat', 'It is brighter', 'It is colder'], answer: 'It wastes less energy as heat', hint1: 'Less heat = more useful', hint2: 'Filament got hot', hint3: 'Linked to dissipation' },
      { id: 'phys-8.3.4', type: 'number', question: 'Engine: 1000 J in, 250 J useful. Wasted (J)?', answer: '750', hint1: 'Subtract', hint2: 'Three digits', hint3: '1000 − 250' },
    ],
    level3: [
      { id: 'phys-8.3.5', type: 'choice', question: 'Why can no machine ever be 100% efficient in practice?', options: ['Wasted energy is always created somewhere', 'Friction and air resistance dissipate heat', 'Conservation forbids it', 'Random'], answer: 'Friction and air resistance dissipate heat', hint1: 'Real-world losses', hint2: 'Always some heat', hint3: 'Practical cap' },
      { id: 'phys-8.3.6', type: 'text', question: 'Energy spread to the surroundings is said to be ___ .', answer: 'dissipated', hint1: '10 letters', hint2: 'Spread out', hint3: 'Starts with d' },
    ],
  },
};

// =====================================================================
// 8.4  ENERGY RESOURCES
// =====================================================================
const RESOURCES_TOPIC = {
  id: 'phys-8.4',
  title: 'Energy Resources',
  slug: 'energy-resources',
  pageRef: 'Section 8 · Topic 4',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Renewable vs non-renewable',
        body:
          'A renewable resource will never run out (or replenishes faster than we use it). Non-renewable resources are finite and will eventually be exhausted. Renewables: solar, wind, hydroelectric, geothermal, tidal, wave, biomass. Non-renewables: coal, oil, natural gas (the fossil fuels) and nuclear (uranium is finite).',
        check: {
          question: 'Which is a NON-renewable energy source?',
          options: ['Solar', 'Wind', 'Coal', 'Tidal'],
          answer: 'Coal',
          explainer: 'Coal is a fossil fuel — once burnt, gone forever (on human timescales).',
        },
      },
      {
        heading: 'Pros and cons',
        body:
          'Fossil fuels: cheap, reliable, easy to store — BUT release CO₂ and other pollutants and will run out. Nuclear: low CO₂, high energy density — BUT radioactive waste lasts thousands of years and accidents are catastrophic. Wind/solar: zero CO₂ in operation, free fuel — BUT intermittent (no wind, no power; no sun, no power). Hydro: reliable and powerful — BUT floods valleys to make reservoirs.',
        check: {
          question: 'A major drawback of wind power is…',
          options: ['It releases CO₂', 'It is expensive to operate', 'It is intermittent (only works when windy)', 'It needs uranium'],
          answer: 'It is intermittent (only works when windy)',
          explainer: 'No wind = no power. That\u2019s why grids combine multiple renewables and battery storage.',
        },
      },
      {
        heading: 'The mix today',
        body:
          'The UK has shifted dramatically over the last 20 years: in 2000, ~75% of electricity came from coal/gas; today, renewables (wind especially) plus nuclear and gas account for the bulk, with coal nearly phased out. The aim is to keep cutting fossil-fuel use to reduce CO₂ emissions.',
        check: {
          question: 'Switching from coal to renewable electricity helps reduce…',
          options: ['Light', 'CO₂ emissions', 'Wind', 'Wages'],
          answer: 'CO₂ emissions',
          explainer: 'Coal releases huge amounts of CO₂; wind/solar release none.',
        },
      },
    ],
    keyPoints: [
      'Renewable: won\u2019t run out (solar, wind, hydro, tidal, wave, geothermal, biomass)',
      'Non-renewable: finite (coal, oil, gas, nuclear)',
      'Fossil fuels: reliable but emit CO₂ and pollute',
      'Nuclear: low CO₂ but waste is dangerous and long-lived',
      'Wind/solar: zero CO₂ but intermittent',
      'UK is moving from fossils to renewables to cut emissions',
    ],
    workedExamples: [
      {
        problem: 'Compare wind power and natural gas as electricity sources. Give one advantage and one disadvantage of each.',
        steps: [
          { step: 1, instruction: 'Wind +', working: 'Zero CO₂ when running, free fuel' },
          { step: 2, instruction: 'Wind −', working: 'Intermittent — varies with weather' },
          { step: 3, instruction: 'Gas +', working: 'Reliable, can be turned up/down to match demand' },
          { step: 4, instruction: 'Gas −', working: 'Releases CO₂; finite resource' },
        ],
        answer: 'Wind: clean but unreliable. Gas: reliable but emits CO₂.',
        commonMistake: 'Calling nuclear "renewable" — uranium is finite. Nuclear has the LOW CO₂ benefit, but it isn\u2019t renewable.',
      },
    ],
    whyItWorks:
      'No single energy source is perfect. Real-world energy systems mix several to balance reliability, cost and emissions. As batteries and grid technology improve, the share of renewables can keep rising.',
  },
  glossary: [
    { term: 'Renewable', definition: 'Energy resource that won\u2019t run out (e.g. wind, solar).' },
    { term: 'Non-renewable', definition: 'Energy resource that is finite (e.g. coal, oil, gas, uranium).' },
    { term: 'Fossil fuel', definition: 'Coal, oil or natural gas — formed from prehistoric organisms.' },
    { term: 'Intermittent', definition: 'Not always available (e.g. solar at night, wind when calm).' },
    { term: 'Biomass', definition: 'Energy from burning recently-living material (wood, crops, etc.).' },
  ],
  examQuestion: {
    prompt:
      "(a) Define renewable. (b) Name TWO non-renewable resources. (c) Compare solar and gas as electricity sources, giving one advantage and disadvantage of each. (5 marks)",
    maxMarks: 5,
    markSchemePoints: [
      'Renewable = a resource that won\u2019t run out / replenishes faster than used',
      'Any 2 of: coal, oil, natural gas, nuclear (uranium)',
      'Solar +: zero CO₂ / free fuel',
      'Solar −: intermittent / no output at night',
      'Gas +: reliable, controllable; Gas −: emits CO₂ / finite',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-8.4.w1', type: 'text', question: 'Coal, oil and gas are ___ fuels.', answer: 'fossil', hint1: '6 letters', hint2: 'From prehistoric remains', hint3: 'Starts with f' },
      { id: 'phys-8.4.w2', type: 'choice', question: 'Which is renewable?', options: ['Coal', 'Gas', 'Wind', 'Uranium'], answer: 'Wind', hint1: 'Air movement', hint2: '4 letters', hint3: 'Won\u2019t run out' },
      { id: 'phys-8.4.w3', type: 'choice', question: 'A drawback of wind power is…', options: ['Releases CO₂', 'Needs sunlight', 'It is intermittent', 'Causes earthquakes'], answer: 'It is intermittent', hint1: 'Variable output', hint2: 'Calm = no power', hint3: 'Weather-dependent' },
    ],
    level1: [
      { id: 'phys-8.4.1', type: 'choice', question: 'Which is NOT renewable?', options: ['Hydro', 'Geothermal', 'Tidal', 'Nuclear'], answer: 'Nuclear', hint1: 'Uses uranium', hint2: 'Finite resource', hint3: '7 letters' },
      { id: 'phys-8.4.2', type: 'text', question: 'Energy from burning recently-living material (e.g. wood, crops) is called ___ .', answer: 'biomass', hint1: '7 letters', hint2: 'Bio + mass', hint3: 'Starts with b' },
    ],
    level2: [
      { id: 'phys-8.4.3', type: 'choice', question: 'A pro of nuclear vs coal is…', options: ['Cheaper to build', 'Lower CO₂ emissions', 'Easier to store waste', 'No risk of accidents'], answer: 'Lower CO₂ emissions', hint1: 'Clean for the climate', hint2: 'Carbon-free generation', hint3: 'Linked to greenhouse gas' },
      { id: 'phys-8.4.4', type: 'choice', question: 'A drawback of hydroelectric dams is…', options: ['They release CO₂', 'They flood valleys to make reservoirs', 'They produce nuclear waste', 'They depend on the Sun'], answer: 'They flood valleys to make reservoirs', hint1: 'Land use', hint2: 'Habitat displacement', hint3: 'Local environmental cost' },
    ],
    level3: [
      { id: 'phys-8.4.5', type: 'choice', question: 'Why must a national grid combine multiple energy resources?', options: ['It looks better', 'To balance reliability, cost and emissions', 'To use more wires', 'For aesthetic reasons'], answer: 'To balance reliability, cost and emissions', hint1: 'No single source is perfect', hint2: 'Risk diversification', hint3: 'Trade-offs across resources' },
      { id: 'phys-8.4.6', type: 'text', question: 'Energy from heat deep inside the Earth is called ___ .', answer: 'geothermal', hint1: '10 letters', hint2: 'Geo + thermal', hint3: 'Starts with g' },
    ],
  },
};

// =====================================================================
// 8.5  GENERATING ELECTRICITY
// =====================================================================
const GENERATING_TOPIC = {
  id: 'phys-8.5',
  title: 'Generating Electricity',
  slug: 'generating-electricity',
  pageRef: 'Section 8 · Topic 5',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Power station basics',
        body:
          'Almost every large power station works the same way: an energy source is used to spin a turbine, which spins a generator, which produces electricity. The energy source is the only thing that varies — coal/gas burned to boil water → steam spins the turbine; in nuclear, the heat comes from nuclear reactions; in hydro, falling water spins the turbine directly; in wind, moving air does the spinning.',
        check: {
          question: 'In a coal power station, what spins the turbine?',
          options: ['Burning coal directly', 'Steam from boiled water', 'Falling water', 'Wind'],
          answer: 'Steam from boiled water',
          explainer: 'Coal heats water to make steam; the steam under pressure spins the turbine.',
        },
      },
      {
        heading: 'Generators in detail',
        body:
          'A generator works by spinning a coil of wire inside a magnetic field. The motion of the coil through the field induces an electric current — that current is then sent down the wires of the National Grid. So in EVERY case, the original energy source ends up as kinetic energy of a spinning coil, then electrical energy.',
        check: {
          question: 'How does a generator turn motion into electricity?',
          options: ['Burning fuel', 'Spinning a coil of wire in a magnetic field', 'Heating up wires', 'Cooling down water'],
          answer: 'Spinning a coil of wire in a magnetic field',
          explainer: 'Electromagnetic induction — moving a wire through a field makes a current.',
        },
      },
      {
        heading: 'Solar panels (the exception)',
        body:
          'Solar PV panels are different — they use the photovoltaic effect to turn light energy directly into electrical current, without any moving turbine. The photons knock electrons free in the silicon panel, creating a current. Solar panels therefore have no moving parts, making them low-maintenance.',
        check: {
          question: 'Solar PV panels generate electricity by…',
          options: ['Boiling water to make steam', 'A photovoltaic effect — light directly creates current', 'Wind hitting them', 'Burning gas'],
          answer: 'A photovoltaic effect — light directly creates current',
          explainer: 'No turbine involved. Light → electrons → current. Done.',
        },
      },
    ],
    keyPoints: [
      'Most power stations: source → spin turbine → spin generator → electricity',
      'Coal/gas/nuclear/biomass all heat water to make steam',
      'Hydro/wind/tidal use moving fluid to spin the turbine directly',
      'Generator = coil of wire spun in a magnetic field → induces current',
      'Solar PV is the exception: light → current via photovoltaic effect (no turbine)',
    ],
    workedExamples: [
      {
        problem: 'Describe how electricity is generated in (a) a wind turbine, (b) a coal power station.',
        steps: [
          { step: 1, instruction: '(a) Wind turbine', working: 'Wind turns blades → spins a generator coil in a field → induces current' },
          { step: 2, instruction: '(b) Coal station', working: 'Coal burned → heats water → steam at high pressure → spins turbine → spins generator → induces current' },
        ],
        answer: 'Wind = direct mechanical spin. Coal = heat → steam → mechanical spin.',
        commonMistake: 'Saying "the wind makes electricity" — it\u2019s the generator that makes electricity; wind just supplies the spin.',
      },
    ],
    whyItWorks:
      'Once you spot that almost every power station is "spin a magnet near a coil", the whole system is easy to understand. Engineers vary only HOW you spin the magnet — that\u2019s the only difference between a coal plant and a wind farm.',
  },
  glossary: [
    { term: 'Turbine', definition: 'A bladed wheel that spins when fluid (steam, water, air) flows through it.' },
    { term: 'Generator', definition: 'A device that converts kinetic energy into electrical energy (coil + magnetic field).' },
    { term: 'Photovoltaic effect', definition: 'Light hitting silicon panels frees electrons, creating a current.' },
    { term: 'National Grid', definition: 'The network of power stations and cables that delivers electricity nationwide.' },
  ],
  examQuestion: {
    prompt:
      "(a) Describe the energy transfers when a coal power station generates electricity. (b) Explain why solar PV panels don't need a turbine. (5 marks)",
    maxMarks: 5,
    markSchemePoints: [
      'Chemical store of coal → thermal store of water (heating)',
      'Thermal → kinetic of steam → kinetic of turbine and generator',
      'Kinetic → electrical (in generator)',
      'Solar PV uses the photovoltaic effect',
      'Light energy directly knocks electrons in silicon, creating a current — no moving parts needed',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-8.5.w1', type: 'text', question: 'A bladed wheel spun by fluid is called a ___ .', answer: 'turbine', hint1: '7 letters', hint2: 'Wind, steam, water', hint3: 'Starts with t' },
      { id: 'phys-8.5.w2', type: 'choice', question: 'Coal stations spin their turbines using…', options: ['Wind', 'Falling water', 'Steam', 'Light'], answer: 'Steam', hint1: 'Boiled water', hint2: '5 letters', hint3: 'High-pressure gas' },
      { id: 'phys-8.5.w3', type: 'choice', question: 'A solar PV panel needs…', options: ['Steam', 'A turbine', 'Sunlight', 'Wind'], answer: 'Sunlight', hint1: 'Photovoltaic effect', hint2: '8 letters', hint3: 'Starts with s' },
    ],
    level1: [
      { id: 'phys-8.5.1', type: 'choice', question: 'A generator is essentially…', options: ['A burner', 'A coil of wire spun in a magnetic field', 'A solar panel', 'A wire heated up'], answer: 'A coil of wire spun in a magnetic field', hint1: 'Electromagnetic induction', hint2: 'Magnet + coil', hint3: 'Spinning is the key' },
      { id: 'phys-8.5.2', type: 'text', question: 'In nuclear power stations, the heat is used to make ___ that spins the turbine.', answer: 'steam', hint1: '5 letters', hint2: 'Same as coal stations', hint3: 'Boiled water' },
    ],
    level2: [
      { id: 'phys-8.5.3', type: 'choice', question: 'In a wind turbine, the source energy is…', options: ['Solar light', 'Kinetic of moving air', 'Chemical', 'Thermal'], answer: 'Kinetic of moving air', hint1: 'Wind = moving air', hint2: 'KE store', hint3: 'Mechanical' },
      { id: 'phys-8.5.4', type: 'choice', question: 'Why do solar PV panels have no moving parts?', options: ['Better to look at', 'Light directly creates current — no need for turbine', 'They are small', 'They are cheap'], answer: 'Light directly creates current — no need for turbine', hint1: 'Photovoltaic shortcut', hint2: 'Skips kinetic step', hint3: 'Solid-state' },
    ],
    level3: [
      { id: 'phys-8.5.5', type: 'choice', question: 'The full chain in a gas station is…', options: ['Chemical → kinetic → electrical', 'Chemical → thermal (steam) → kinetic → electrical', 'Light → electrical', 'Nuclear → light'], answer: 'Chemical → thermal (steam) → kinetic → electrical', hint1: '4 stages', hint2: 'Burn → boil → spin → induce', hint3: 'Same as coal' },
      { id: 'phys-8.5.6', type: 'text', question: 'The network of power stations and cables across the country is called the National ___ .', answer: 'Grid', hint1: '4 letters', hint2: 'Map-like network', hint3: 'Starts with G' },
    ],
  },
};

// =====================================================================
// 8.6  THE COST OF ELECTRICITY
// =====================================================================
const COST_TOPIC = {
  id: 'phys-8.6',
  title: 'The Cost of Electricity',
  slug: 'cost-of-electricity',
  pageRef: 'Section 8 · Topic 6',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Energy used at home',
        body:
          'Energy companies don\u2019t bill you in joules — too many. They use kilowatt-hours (kWh). 1 kWh = the energy used by a 1 kW (1000 W) appliance running for 1 hour. The "energy meter" at your house is really an energy COUNTER. The bigger the wattage and the longer it\u2019s on, the more kWh you use — and the bigger the bill.',
        check: {
          question: 'A 2 kW heater runs for 3 hours. Energy used (kWh)?',
          options: ['2', '3', '5', '6'],
          answer: '6',
          explainer: 'Energy = power × time = 2 kW × 3 h = 6 kWh.',
        },
      },
      {
        heading: 'Calculating cost',
        body:
          'Cost of running an appliance = energy used (kWh) × unit price (p/kWh). Example: at a UK rate of 25p per kWh, running a 2 kW heater for 4 hours costs 2 × 4 × 25 = 200p (= £2.00). Pupils LOVE this calculation because it instantly shows them why parents fuss about the heating!',
        check: {
          question: 'A 1 kW kettle runs 0.5 hour at 25p/kWh. Cost?',
          options: ['12.5p', '25p', '50p', '£1.25'],
          answer: '12.5p',
          explainer: '1 × 0.5 × 25 = 12.5p.',
        },
      },
      {
        heading: 'Why it matters',
        body:
          'Knowing how to estimate cost lets you make smarter choices. Switching from a 60 W filament bulb to a 6 W LED saves (60 − 6) × hours × rate over a year — easily £20+ per bulb in a busy house. Insulation, lower thermostat, washing at 30 °C and shorter showers all chop kWh and so cut bills AND CO₂ at the same time.',
        check: {
          question: 'Switching from a 60 W to a 6 W bulb saves what % of energy?',
          options: ['10%', '50%', '90%', '100%'],
          answer: '90%',
          explainer: '6/60 = 0.1, so the LED uses 10% — saving 90%.',
        },
      },
    ],
    keyPoints: [
      'Energy at home is measured in kilowatt-hours (kWh)',
      '1 kWh = 1 kW for 1 hour (= 3,600,000 J)',
      'Energy = power (kW) × time (h)',
      'Cost = energy (kWh) × price per kWh',
      'Lower-wattage appliances + shorter use = smaller bills + less CO₂',
    ],
    workedExamples: [
      {
        problem: 'A family uses a 2.5 kW oven for 1.5 hours and a 1 kW iron for 0.5 hour. At 30p/kWh, total cost?',
        steps: [
          { step: 1, instruction: 'Oven energy', working: '2.5 × 1.5 = 3.75 kWh' },
          { step: 2, instruction: 'Iron energy', working: '1 × 0.5 = 0.5 kWh' },
          { step: 3, instruction: 'Total energy', working: '3.75 + 0.5 = 4.25 kWh' },
          { step: 4, instruction: 'Cost', working: '4.25 × 30 = 127.5p ≈ £1.28' },
        ],
        answer: '£1.28 (approx).',
        commonMistake: 'Using watts instead of kilowatts in the formula. Always convert to kW first.',
      },
    ],
    whyItWorks:
      'Hidden behind every electric appliance is a tiny meter ticking up the kWh. Mastering the simple "P × t × price" formula is one of the most useful real-world physics skills — you can reason about energy bills, choose efficient gear, and spot rip-offs.',
  },
  glossary: [
    { term: 'Kilowatt-hour (kWh)', definition: 'The energy used by a 1 kW appliance running for 1 hour. The "unit" on energy bills.' },
    { term: 'Power', definition: 'Energy transferred per second; measured in watts (W) or kilowatts (kW).' },
    { term: 'Tariff', definition: 'The price an energy company charges per kWh.' },
  ],
  examQuestion: {
    prompt:
      'A 2 kW kettle is used 3 times a day, each time for 5 minutes. The unit cost is 30p/kWh. (a) Calculate the energy used in one day (kWh). (b) Calculate the daily cost. (c) Suggest one way to cut this cost. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Time per use = 5/60 = 1/12 h, so 3/12 = 0.25 h per day',
      'Energy = 2 × 0.25 = 0.5 kWh',
      'Cost = 0.5 × 30 = 15p',
      'Boil only as much water as needed (less time)',
      'OR use a more efficient kettle / boil less often / use a flask to keep water hot (any sensible)',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-8.6.w1', type: 'text', question: 'Energy at home is sold in ___ .', answer: 'kWh', hint1: '3 letters/units', hint2: 'Kilowatt-hour', hint3: 'Symbol' },
      { id: 'phys-8.6.w2', type: 'number', question: '3 kW × 2 h = ? kWh', answer: '6', hint1: 'Multiply', hint2: 'Single digit', hint3: 'Six' },
      { id: 'phys-8.6.w3', type: 'number', question: 'Energy = 4 kWh, cost 30p/kWh. Total cost (p)?', answer: '120', hint1: '4 × 30', hint2: 'Three digits', hint3: '£1.20' },
    ],
    level1: [
      { id: 'phys-8.6.1', type: 'number', question: 'A 1500 W (1.5 kW) heater on for 4 h. Energy (kWh)?', answer: '6', hint1: 'Convert W → kW first', hint2: '1.5 × 4', hint3: 'Single digit' },
      { id: 'phys-8.6.2', type: 'choice', question: 'To cut electricity costs you should…', options: ['Use higher wattage appliances', 'Run things longer', 'Use lower wattage and run for shorter times', 'Leave lights on'], answer: 'Use lower wattage and run for shorter times', hint1: 'Smaller P × t', hint2: 'Less energy', hint3: 'Less cost' },
    ],
    level2: [
      { id: 'phys-8.6.3', type: 'number', question: 'A 60 W bulb on for 5 h. Energy (kWh)?', answer: '0.3', hint1: '0.06 × 5', hint2: 'One decimal', hint3: '0.3' },
      { id: 'phys-8.6.4', type: 'number', question: '0.5 kWh × 28p/kWh = cost (p)?', answer: '14', hint1: 'Multiply', hint2: 'Two digits', hint3: '14p' },
    ],
    level3: [
      { id: 'phys-8.6.5', type: 'choice', question: 'A 6 W LED replaces a 60 W bulb running 5 h/day at 30p/kWh. Approx daily saving?', options: ['1p', '5p', '8p', '15p'], answer: '8p', hint1: 'Save 54 W = 0.054 kW × 5 h × 30p', hint2: '~8p/day', hint3: 'Annual saving ~£30' },
      { id: 'phys-8.6.6', type: 'text', question: 'The price an energy company charges per kWh is called the ___ .', answer: 'tariff', hint1: '6 letters', hint2: 'Pricing term', hint3: 'Starts with t' },
    ],
  },
};

// =====================================================================
// 8.7  COMPARING POWER RATINGS AND ENERGY VALUES
// =====================================================================
const POWER_TOPIC = {
  id: 'phys-8.7',
  title: 'Comparing Power Ratings and Energy Values',
  slug: 'power-ratings-and-energy-values',
  pageRef: 'Section 8 · Topic 7',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Power ratings',
        body:
          'Every electrical appliance has a power rating in watts (W) or kilowatts (kW). Power = energy transferred per second. A 2000 W (2 kW) kettle transfers 2000 J of energy every second. Comparing power ratings lets you predict which appliance is "thirstier" for energy. A high-power appliance run for a short time can still use a lot of energy.',
        check: {
          question: 'A 2000 W kettle transfers ___ J every second.',
          options: ['200', '2000', '20000', '20'],
          answer: '2000',
          explainer: 'Power in watts = joules per second.',
        },
      },
      {
        heading: 'Calculating energy from power and time',
        body:
          'Energy (J) = Power (W) × Time (s). Or in big units: Energy (kWh) = Power (kW) × Time (h). A 100 W lamp on for 2 hours: Energy = 100 × 2 × 3600 = 720,000 J = 0.2 kWh. The two formulas are the same idea — just different units.',
        check: {
          question: 'A 200 W TV is on for 3 hours. Energy (kWh)?',
          options: ['0.2', '0.6', '1', '600'],
          answer: '0.6',
          explainer: '0.2 kW × 3 h = 0.6 kWh.',
        },
      },
      {
        heading: 'Energy values of foods and fuels',
        body:
          'Different fuels and foods carry different amounts of energy. 1 g of fat releases ~37 kJ when burnt; 1 g of carbohydrate or protein ~17 kJ; 1 g of petrol ~46 kJ; 1 g of TNT only ~4 kJ (TNT is BIG because it releases its energy fast, not because it has lots). When comparing, ask: how much energy per gram (or per litre, or per kg)?',
        check: {
          question: 'Gram-for-gram, which has the MOST energy?',
          options: ['Carbohydrate', 'Protein', 'Fat', 'TNT'],
          answer: 'Fat',
          explainer: '~37 kJ/g — more than double carbs/protein.',
        },
      },
    ],
    keyPoints: [
      'Power in watts = joules per second',
      'Energy (J) = Power (W) × Time (s)',
      'Energy (kWh) = Power (kW) × Time (h)',
      'Higher power → more energy per second',
      'Energy per gram (kJ/g) lets you compare fuels and foods',
      'Fat ~37 kJ/g; carbs/protein ~17 kJ/g; petrol ~46 kJ/g',
    ],
    workedExamples: [
      {
        problem: 'A 1500 W microwave runs for 90 seconds. Energy used in joules?',
        steps: [
          { step: 1, instruction: 'Identify formula', working: 'Energy = power × time' },
          { step: 2, instruction: 'Same units', working: 'Both already in W and s' },
          { step: 3, instruction: 'Calculate', working: '1500 × 90 = 135,000 J = 135 kJ' },
        ],
        answer: '135,000 J (135 kJ).',
        commonMistake: 'Mixing units. If power is in W use seconds; if power is in kW use hours.',
      },
    ],
    whyItWorks:
      'Power and energy aren\u2019t the same. Power is the RATE of transfer (joules per second). Energy is the total over time. A bicycle and a car both can produce power, but the car can hold its higher rate for far longer — so it does much more total work.',
  },
  glossary: [
    { term: 'Power', definition: 'Energy transferred per second; measured in watts (W).' },
    { term: 'Watt (W)', definition: 'The unit of power. 1 W = 1 J per second.' },
    { term: 'Energy density', definition: 'The energy per unit mass (or volume) of a fuel/food.' },
    { term: 'Kilojoule (kJ)', definition: '1000 joules — common unit on food packets.' },
  ],
  examQuestion: {
    prompt:
      'A hairdryer is rated 1.8 kW. (a) Explain what 1.8 kW means in joules per second. (b) Calculate the energy used in 5 minutes (J). (c) State which has more energy gram-for-gram: fat or carbohydrate. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      '1.8 kW = 1800 W = 1800 J transferred per second',
      'Time = 5 × 60 = 300 s',
      'Energy = 1800 × 300 = 540,000 J',
      'Or 0.15 kWh',
      'Fat has more energy per gram (~37 kJ/g) than carbohydrate (~17 kJ/g)',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-8.7.w1', type: 'text', question: '1 watt = 1 ___ per second.', answer: 'joule', hint1: '5 letters', hint2: 'Energy unit', hint3: 'Starts with j' },
      { id: 'phys-8.7.w2', type: 'number', question: 'Energy = 100 W × 60 s = ? J', answer: '6000', hint1: 'Multiply', hint2: 'Four digits', hint3: '6 × 1000' },
      { id: 'phys-8.7.w3', type: 'choice', question: 'Highest energy per gram?', options: ['Carbohydrate', 'Protein', 'Fat', 'Water'], answer: 'Fat', hint1: '~37 kJ/g', hint2: '3 letters', hint3: 'Best calorie store' },
    ],
    level1: [
      { id: 'phys-8.7.1', type: 'number', question: 'A 50 W bulb on 200 s. Energy (J)?', answer: '10000', hint1: '50 × 200', hint2: 'Five digits', hint3: '10 × 1000' },
      { id: 'phys-8.7.2', type: 'choice', question: 'A 2 kW heater for 30 min uses…', options: ['0.5 kWh', '1 kWh', '2 kWh', '4 kWh'], answer: '1 kWh', hint1: 'Convert min → h', hint2: '2 × 0.5', hint3: 'Half hour' },
    ],
    level2: [
      { id: 'phys-8.7.3', type: 'number', question: 'A 60 W bulb on for 1 hour. Energy (J)? (Hint: 1 hour = 3600 s)', answer: '216000', hint1: '60 × 3600', hint2: 'Six digits', hint3: '216,000' },
      { id: 'phys-8.7.4', type: 'choice', question: 'Why is fat a better long-term energy store than carbs?', options: ['Tastes better', 'More energy per gram', 'Easier to digest', 'Lighter'], answer: 'More energy per gram', hint1: 'Energy density', hint2: 'kJ/g', hint3: '~2× carbs' },
    ],
    level3: [
      { id: 'phys-8.7.5', type: 'number', question: 'Petrol gives ~46 kJ/g. Energy in 100 g (kJ)?', answer: '4600', hint1: 'Multiply', hint2: 'Four digits', hint3: '46 × 100' },
      { id: 'phys-8.7.6', type: 'text', question: 'Energy per gram (or kg) of a fuel is its energy ___ .', answer: 'density', hint1: '7 letters', hint2: 'Same word as mass density', hint3: 'Starts with d' },
    ],
  },
};

// =====================================================================
// 8.8  PHYSICAL CHANGES
// =====================================================================
const PHYSICAL_CHANGES_TOPIC = {
  id: 'phys-8.8',
  title: 'Physical Changes',
  slug: 'physical-changes',
  pageRef: 'Section 8 · Topic 8',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'What is a physical change?',
        body:
          'A physical change does NOT make a new substance. The atoms or molecules are unchanged. Examples: melting ice, dissolving sugar, cutting paper, stretching a spring. Physical changes are usually reversible — you can usually get back to the starting substance. They contrast with chemical changes (covered in Chemistry Section 6), which DO make new substances.',
        check: {
          question: 'Which of these is a physical change?',
          options: ['Burning paper', 'Rusting iron', 'Melting ice', 'Cooking egg'],
          answer: 'Melting ice',
          explainer: 'Same molecules (H₂O) before and after — no new substance. The other three are chemical.',
        },
      },
      {
        heading: 'State changes are physical',
        body:
          'Melting, freezing, boiling, condensing, evaporating and subliming are all physical changes — the substance keeps the same molecules; only the arrangement and motion of particles changes. Heating provides the energy to overcome attractive forces between particles, allowing them to move further apart.',
        check: {
          question: 'When water boils to steam, the molecules…',
          options: ['Become a different substance', 'Are unchanged but move much further apart', 'Become smaller', 'Disappear'],
          answer: 'Are unchanged but move much further apart',
          explainer: 'Same H₂O molecules, just spaced out and moving fast.',
        },
      },
      {
        heading: 'Energy and temperature',
        body:
          'Heating a substance gives its particles more energy. In a solid, that energy goes into faster vibrations. In a liquid, faster random motion. In a gas, even faster random motion plus more space between particles. During a state change (e.g. melting), the energy goes into BREAKING bonds rather than raising temperature — that\u2019s why a heating curve is FLAT during melting and boiling.',
        check: {
          question: 'During melting, the energy supplied is used mainly to…',
          options: ['Heat the solid up', 'Break the forces between particles', 'Cool down the surroundings', 'Make new molecules'],
          answer: 'Break the forces between particles',
          explainer: 'Temperature stays flat at the melting point until all the bonds are broken.',
        },
      },
    ],
    keyPoints: [
      'Physical change = no new substance; reversible',
      'State changes (melt, freeze, boil, etc.) are physical',
      'Atoms/molecules are unchanged',
      'Heating = more particle energy → faster motion',
      'During state change, energy breaks bonds — temp flat on the heating curve',
    ],
    workedExamples: [
      {
        problem: 'Decide whether each is physical or chemical: (i) bending wire, (ii) burning wood, (iii) condensing steam, (iv) baking bread.',
        steps: [
          { step: 1, instruction: 'Bending wire', working: 'No new substance → physical' },
          { step: 2, instruction: 'Burning wood', working: 'New substances (CO₂, water vapour, ash) → chemical' },
          { step: 3, instruction: 'Condensing steam', working: 'Same H₂O → physical' },
          { step: 4, instruction: 'Baking bread', working: 'New molecules form (gluten changes, CO₂ from yeast etc.) → chemical' },
        ],
        answer: 'Physical: bending wire, condensing steam. Chemical: burning wood, baking bread.',
        commonMistake: 'Calling baking "physical" because dough still looks like dough — chemistry happens inside.',
      },
    ],
    whyItWorks:
      'Knowing whether a change is physical or chemical tells you whether it\u2019s reversible, whether mass is conserved in any obvious way, and how to think about the energy involved.',
  },
  glossary: [
    { term: 'Physical change', definition: 'A change with no new substance; usually reversible.' },
    { term: 'Chemical change', definition: 'A change that produces one or more new substances; usually hard to reverse.' },
    { term: 'State change', definition: 'A physical change between solid, liquid and gas states.' },
    { term: 'Heating curve', definition: 'A graph of temperature vs. time/heat — flat during state changes.' },
  ],
  examQuestion: {
    prompt:
      "(a) State TWO differences between a physical and a chemical change. (b) Explain why temperature stays constant while ice melts. (4 marks)",
    maxMarks: 4,
    markSchemePoints: [
      'Physical: no new substance / reversible',
      'Chemical: new substance(s) made / hard to reverse',
      'During melting, energy is used to break bonds between particles',
      'rather than to raise temperature → flat region on graph',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-8.8.w1', type: 'choice', question: 'Which is a physical change?', options: ['Burning gas', 'Melting wax', 'Cooking egg', 'Rusting iron'], answer: 'Melting wax', hint1: 'No new substance', hint2: 'Reversible by cooling', hint3: 'Same wax' },
      { id: 'phys-8.8.w2', type: 'text', question: 'Solid → liquid is called ___ .', answer: 'melting', hint1: '7 letters', hint2: 'Ice → water', hint3: 'Starts with m' },
      { id: 'phys-8.8.w3', type: 'choice', question: 'Steam → water is which physical change?', options: ['Boiling', 'Condensing', 'Melting', 'Subliming'], answer: 'Condensing', hint1: 'Cooling gas', hint2: '10 letters', hint3: 'On a cold window' },
    ],
    level1: [
      { id: 'phys-8.8.1', type: 'choice', question: 'Why are physical changes USUALLY reversible?', options: ['Mass changes', 'Same atoms/molecules involved', 'Random', 'Temperature dependence only'], answer: 'Same atoms/molecules involved', hint1: 'No chemistry', hint2: 'Just rearrangement', hint3: 'Substance unchanged' },
      { id: 'phys-8.8.2', type: 'text', question: 'Heating gives particles more ___ energy.', answer: 'kinetic', hint1: '7 letters', hint2: 'Speed-related', hint3: 'Starts with k' },
    ],
    level2: [
      { id: 'phys-8.8.3', type: 'choice', question: 'On a heating curve, the flat sections happen at the…', options: ['Random points', 'Melting and boiling points', 'Cooling stages only', 'End of the experiment'], answer: 'Melting and boiling points', hint1: 'Temperature stops rising', hint2: 'Energy breaks bonds', hint3: 'State changes' },
      { id: 'phys-8.8.4', type: 'choice', question: 'Cutting a sheet of paper is…', options: ['Physical', 'Chemical', 'Both', 'Neither'], answer: 'Physical', hint1: 'Same paper, smaller pieces', hint2: 'No new molecules', hint3: 'Linked to mechanical change' },
    ],
    level3: [
      { id: 'phys-8.8.5', type: 'choice', question: 'Why does sublimation count as a physical change?', options: ['It makes new molecules', 'Same molecules go straight from solid → gas', 'It is chemistry', 'It loses mass'], answer: 'Same molecules go straight from solid → gas', hint1: 'No new substance', hint2: 'Just state', hint3: 'CO₂ → CO₂(g)' },
      { id: 'phys-8.8.6', type: 'text', question: 'A physical change between solid, liquid and gas is called a ___ change.', answer: 'state', hint1: '5 letters', hint2: 'Phase term', hint3: 'Starts with s' },
    ],
  },
};

// =====================================================================
// 8.9  MOVEMENT OF PARTICLES
// =====================================================================
const MOVEMENT_TOPIC = {
  id: 'phys-8.9',
  title: 'Movement of Particles',
  slug: 'movement-of-particles',
  pageRef: 'Section 8 · Topic 9',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Diffusion',
        body:
          'Particles in liquids and gases move randomly because of their kinetic energy. Diffusion is the spreading of particles from a region of high concentration to a region of low concentration. It happens fastest in gases (think perfume across a room) because gas particles move quickest and have the most empty space to spread through. Slower in liquids; almost nil in solids.',
        check: {
          question: 'Diffusion is fastest in…',
          options: ['Solids', 'Liquids', 'Gases', 'Solids and liquids equally'],
          answer: 'Gases',
          explainer: 'Gas particles move fastest and freely → quickest spread.',
        },
      },
      {
        heading: 'Brownian motion',
        body:
          "Look at smoke trapped under a microscope. The visible smoke specks jiggle randomly because they keep getting hit by smaller, faster, INVISIBLE air particles. This is called Brownian motion, after Robert Brown who first noticed it in 1827. It is direct evidence that particles really exist and are constantly moving in liquids and gases.",
        check: {
          question: 'Brownian motion is evidence that…',
          options: ['Solids never move', 'Tiny invisible particles in fluids constantly hit larger particles', 'Particles are flat', 'Smoke is alive'],
          answer: 'Tiny invisible particles in fluids constantly hit larger particles',
          explainer: 'Random jiggle of visible particles = pushed by even smaller fast-moving particles around them.',
        },
      },
      {
        heading: 'Effect of temperature and concentration',
        body:
          'Higher temperature → particles have more kinetic energy → move faster → diffuse more quickly. A bigger concentration difference → faster diffusion (more "downhill" to spread). That\u2019s why you smell hot food faster than cold food, and why a strong perfume spreads across a room faster than a weak one.',
        check: {
          question: 'Why does perfume diffuse faster on a warm day?',
          options: ['It is heavier', 'Particles move faster at higher temperature', 'Air is thinner', 'It evaporates less'],
          answer: 'Particles move faster at higher temperature',
          explainer: 'More kinetic energy = quicker random motion = faster diffusion.',
        },
      },
    ],
    keyPoints: [
      'Particles in fluids move randomly due to kinetic energy',
      'Diffusion = spreading from high → low concentration',
      'Diffusion fastest in gases, slowest in solids (almost nil)',
      'Brownian motion = visible jiggle = evidence of invisible particles',
      'Hotter = faster diffusion; bigger concentration gap = faster diffusion',
    ],
    workedExamples: [
      {
        problem: 'A teacher opens a perfume bottle at the front of a 10 m room. Predict roughly how long until students at the back smell it. Explain.',
        steps: [
          { step: 1, instruction: 'Mechanism', working: 'Perfume particles enter the air and diffuse randomly' },
          { step: 2, instruction: 'Temperature/density', working: 'Room temperature → typical gas diffusion rates' },
          { step: 3, instruction: 'Air currents', working: 'Air movement (people moving, ventilation) speeds up the spread' },
        ],
        answer: 'Roughly a few minutes (faster if the air is warm and there are convection currents helping). Pure diffusion would take hours; air currents do most of the work.',
        commonMistake: 'Saying "diffusion takes seconds" — pure diffusion is actually slow; mixing of bulk air is what makes the smell move fast.',
      },
    ],
    whyItWorks:
      'Particle motion explains everything from how we smell food to how oxygen reaches alveoli in our lungs. The same simple model — random motion + concentration difference — applies whether the particles are atoms, perfume molecules or pollen grains.',
  },
  glossary: [
    { term: 'Diffusion', definition: 'The spreading of particles from high to low concentration.' },
    { term: 'Brownian motion', definition: 'The visible random jiggle of tiny particles in a fluid, caused by collisions with smaller particles.' },
    { term: 'Concentration', definition: 'The amount of a substance in a given volume.' },
    { term: 'Kinetic energy', definition: 'Energy due to motion of particles.' },
  ],
  examQuestion: {
    prompt:
      '(a) Define diffusion and state the state of matter in which it is fastest. (b) Brownian motion shows tiny particles jiggling. Explain what this proves about matter. (c) State TWO factors that increase the rate of diffusion in a gas. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Diffusion = spreading of particles from high to low concentration',
      'Fastest in gases',
      'Brownian motion = evidence that fluids are made of small, fast-moving particles',
      'These collide with larger visible particles, causing the jiggle',
      'Higher temperature / bigger concentration difference (any two)',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-8.9.w1', type: 'text', question: 'Spreading of particles from high to low concentration is called ___ .', answer: 'diffusion', hint1: '9 letters', hint2: 'Topic title', hint3: 'Starts with d' },
      { id: 'phys-8.9.w2', type: 'choice', question: 'Diffusion is fastest in…', options: ['Solids', 'Liquids', 'Gases', 'Plasma'], answer: 'Gases', hint1: 'Particles move fastest', hint2: '5 letters', hint3: 'Most space between' },
      { id: 'phys-8.9.w3', type: 'choice', question: 'Random jiggle of visible particles in a fluid is called…', options: ['Diffusion', 'Brownian motion', 'Convection', 'Evaporation'], answer: 'Brownian motion', hint1: 'Named after a scientist', hint2: '8 letters first word', hint3: 'Starts with B' },
    ],
    level1: [
      { id: 'phys-8.9.1', type: 'choice', question: 'Heating speeds up diffusion because…', options: ['Heat is mass', 'Particles get more kinetic energy and move faster', 'Particles get bigger', 'Heat repels particles'], answer: 'Particles get more kinetic energy and move faster', hint1: 'Linked to temperature', hint2: 'KE up', hint3: 'Faster motion' },
      { id: 'phys-8.9.2', type: 'text', question: 'Brownian motion was first described by Robert ___ .', answer: 'Brown', hint1: '5 letters', hint2: 'Surname', hint3: 'A colour' },
    ],
    level2: [
      { id: 'phys-8.9.3', type: 'choice', question: 'Why is diffusion almost nil in solids?', options: ['Particles are too big', 'Particles are locked into a fixed position', 'No kinetic energy', 'Solids are cold'], answer: 'Particles are locked into a fixed position', hint1: 'Solid structure', hint2: 'Vibrate but no swap places', hint3: 'No flow' },
      { id: 'phys-8.9.4', type: 'choice', question: 'Why does perfume reach the back of a room faster on a warm day?', options: ['Air is heavier', 'Particles move faster at higher temperature', 'Smaller room', 'No reason'], answer: 'Particles move faster at higher temperature', hint1: 'Heat-KE link', hint2: 'Faster random motion', hint3: 'Diffusion rate up' },
    ],
    level3: [
      { id: 'phys-8.9.5', type: 'choice', question: 'Brownian motion is evidence that…', options: ['Heat is a substance', 'Matter is made of tiny moving particles', 'Smoke is alive', 'Air contains no particles'], answer: 'Matter is made of tiny moving particles', hint1: 'Direct visual proof', hint2: 'Particle theory', hint3: 'Atoms exist + move' },
      { id: 'phys-8.9.6', type: 'text', question: 'A bigger concentration ___ leads to faster diffusion.', answer: 'difference', hint1: '10 letters', hint2: 'Bigger gap', hint3: 'Starts with d' },
    ],
  },
};

// ---------- assemble the section ----------
const BASE_TOPICS = [
  ENERGY_TRANSFER_TOPIC,
  HEATING_TOPIC,
  CONSERVATION_TOPIC,
  RESOURCES_TOPIC,
  GENERATING_TOPIC,
  COST_TOPIC,
  POWER_TOPIC,
  PHYSICAL_CHANGES_TOPIC,
  MOVEMENT_TOPIC,
];

// authoredTopics.json overrides any topic by id (for the PDF pipeline).
export const energyAndMatterTopics = BASE_TOPICS.map((t) =>
  AUTHORED_BY_ID[t.id] ? AUTHORED_BY_ID[t.id] : t,
);
